import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRequestQueryContext } from 'src/features/requests/use-request-query-context';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useEditorReadOnlyAccess } from 'src/features/requests/hooks/use-editor-read-only-access';
import { EditorReadOnlyBadge } from 'src/features/requests/editor-read-only-notice';
import { useCompanyQueryContextData } from 'src/features/companies/use-company-query-context';
import { useProfile } from 'src/models';
import { useRequestStatusMap } from 'src/pages/RequestPage/RequestStatusChangeForm/RequestStatusChangeForm.utils';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import { Button } from 'src/components/ui/button';
import { If } from 'src/components/If';
import { Link } from 'src/components/ui/link';
import { appRoutes } from 'src/routes';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { authQueries } from 'src/api/queries';
import { FileVideo2, FolderInput, Megaphone } from 'lucide-react';
import { getFilenameFromUrl, type DownloadableFile } from 'src/lib/utils/download-utils';
import { DownloadFilesDropdown } from 'src/components/download-files-dropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { RequestPageHeading } from 'src/features/requests/request-page-heading';
import { requestsResponseHelper } from 'src/features/requests/requests-response.helper';
import { DefaultToMultiOutcomeRequestTransformButton } from 'src/features/requests/request-table/default-to-multi-outcome-request-transform-button';
import { Loader2 } from 'src/components/ui/animated-loader2';

const RequestDefaultPageContentTop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();

  const client = useQueryClient();

  const requestQuery = useRequestQueryContext();
  const request = useDecoratedRequestContext();
  const company = useCompanyQueryContextData();
  const isReadOnly = useEditorReadOnlyAccess();

  const user = useProfile({ enabled: false, retry: false });

  const { canChangeStatus } = useRequestStatusMap();

  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();

  // Collect downloadable files from request
  const getDownloadableFiles = useCallback((): DownloadableFile[] => {
    const files: DownloadableFile[] = [];

    // Add media files (use restoration_download_url as fallback for archived files)
    request.media?.forEach((media) => {
      const downloadUrl = media.index_url || media.restoration_download_url;
      if (downloadUrl) {
        files.push({
          url: downloadUrl,
          filename: media.file_name || getFilenameFromUrl(downloadUrl),
          mimeType: media.mime_type || undefined,
        });
      }
    });

    // Add asset URLs (if they are direct file links). Prefer the asset's
    // original client filename (#2465) — for gallery imports the backend
    // coalesces this to the source media's name — and only derive from the
    // hashed storage URL when no name is available.
    request.assets?.forEach((asset, index) => {
      if (asset.url && (asset.url.includes('.') || asset.url.includes('download'))) {
        files.push({
          url: asset.url,
          filename: asset.file_name || getFilenameFromUrl(asset.url, `asset-${index + 1}`),
          mimeType: asset.mime_type || undefined,
        });
      }
    });

    return files;
  }, [request.media, request.assets]);

  const changeStatusButtonProps: React.ComponentProps<typeof Button> = {
    onClick: () =>
      openRequestStatusChangeModal({ requestId: request.id, companyId: company?.id ?? '' }),
    className: 'tw-order-first md:tw-order-last tw-grow sm:tw-grow-0',
    size: 'sm',
  };

  if (user.isAdmin && !request.hasEditor) {
    changeStatusButtonProps['title'] = t(
      "Status is not editable on requests that doesn't have assigned editor.",
    )!;
    changeStatusButtonProps['disabled'] = true;
  }

  if (!canChangeStatus(request?.status)) {
    changeStatusButtonProps['title'] = t("Status can't be changed for completed requests.")!;
    changeStatusButtonProps['disabled'] = true;
  }

  // The revisions page's breadcrumb lands here, so without this a covering
  // editor is one click from the same rejected-button toast #2653 removes:
  // moveToEditing / moveToQueued / requestAttention all run
  // guardEditorOwnsAssignedRequest and answer "This request is assigned to
  // another editor."
  if (isReadOnly) {
    changeStatusButtonProps['title'] = t(
      'This request is assigned to another editor — you can watch it, but not change it',
    )!;
    changeStatusButtonProps['disabled'] = true;
  }

  return (
    <div ref={ref} className={className} {...props}>
      <If
        when={!!children}
        else={
          <div className={'tw-mb-4 tw-flex tw-flex-wrap tw-gap-4'}>
            <RequestPageHeading
              title={request?.title ?? t('unset', { ns: 'common' })!}
              requestId={request.id}
              companyId={company?.id}
              companyName={company?.name}
              currentCrumb={t('common:revisions', {
                defaultValue: 'Revisions',
              })}
            />

            <div
              className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}
            >
              <If when={isReadOnly}>
                <EditorReadOnlyBadge />
              </If>

              <If when={request.canRequestAttention && !isReadOnly}>
                <RequestAttentionAction
                  form={{
                    requestId: request.id,
                    initialValues: {
                      attention_text: request?.attention_text || '',
                    },
                    onSubmit: async ({ attention_text }) => {
                      requestQuery.setQueryData({
                        attention_text,
                        requested_attention_at: new Date().toDateString(),
                      });
                      client.invalidateQueries({
                        queryKey: [authQueries.requests, request?.status],
                      });
                    },
                  }}
                  as={Button}
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Megaphone className={'tw-size-5'} />
                  {t('Request attention')}
                </RequestAttentionAction>
              </If>

              {/* RequestPolicy::transform runs guardEditorOwnsAssignedRequest —
                  turning another editor's request into a multi-outcome project
                  is not a covering editor's call. */}
              <If when={requestsResponseHelper.canBeTransformedToMultiOutcome(request) && !isReadOnly}>
                <DefaultToMultiOutcomeRequestTransformButton
                  size={'iconStartSm'}
                  variant={'outline'}
                  className={'tw-grow sm:tw-grow-0'}
                  request={request}
                >
                  {(props) => (
                    <>
                      {props.isLoading ? (
                        <Loader2 size={16} variant={'unset'} />
                      ) : (
                        <FolderInput size={16} />
                      )}
                      {t('Transform')}
                    </>
                  )}
                </DefaultToMultiOutcomeRequestTransformButton>
              </If>

              {request.hasMedia && (
                <DownloadFilesDropdown
                  getFiles={getDownloadableFiles}
                  zipFileName={`${request.title || 'project'}-files`}
                  className="tw-grow sm:tw-grow-0"
                />
              )}

              <If when={request.hasRevisions}>
                <Button
                  asChild
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Link
                    variant={'unset'}
                    to={generatePath(appRoutes.requestRevisions, {
                      companyId: company?.id ?? '',
                      requestId: request.id,
                    })}
                  >
                    <FileVideo2 className={'tw-size-5'} />
                    {t('Revisions')}
                  </Link>
                </Button>
              </If>

              <Button {...changeStatusButtonProps}>{t('Change status')}</Button>
            </div>
          </div>
        }
      >
        {children}
      </If>
    </div>
  );
});
RequestDefaultPageContentTop.displayName = 'RequestDefaultPageContentTop';

export { RequestDefaultPageContentTop };
